import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities } from '@redux/entities'

import api from '@functions/api'

import type {
  Action, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { AppDispatch } from '@redux/store'

import { BrandGroupModel } from '@models/brandGroup'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/brandGroup/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/brandGroup/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/brandGroup/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/brandGroup/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/brandGroup/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/brandGroup/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/brandGroup/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/brandGroup/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/brandGroup/UPDATE_FAILURE'

export type BrandGroupModuleState = {
  creating: boolean,
  errors: Errors,
  loaded: boolean,
  loadedForKeys: string[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: BrandGroupModuleState = {
  creating: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}): Action {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
  }
}

export function fetchSuccess(): Action {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []): Action {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(): Action {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(): Action {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []): Action {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(): Action {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(): Action {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []): Action {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadBrandGroups(options: EntityOptions = {}) {
  const { entityKey } = options || {}

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().brandGroups.loadedForKeys]
    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/brand_groups.json', options)
      .then(({ data }: { data: BrandGroupModel[] }) => {
        const normalizedJson = normalize(data, Schemas.BRAND_GROUP_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadBrandGroup(brandGroupId: number, options: EntityOptions = {}) {
  const { entityKey } = options || {}

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.brandGroups.loadedForKeys]

    if (entityKey) loadedForKeys.push(entityKey)

    // Set Loading
    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api(`/brand_groups/${brandGroupId}.json`, options)
      .then(({ data }: { data: BrandGroupModel }) => {
        const normalizedJson = normalize(data, Schemas.BRAND_GROUP)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createBrandGroup(
  brandGroup: BrandGroupModel,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      brand_group: brandGroup,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/brand_groups.json', options, config)
      .then(({ data }: { data: BrandGroupModel }) => {
        const normalizedJson = normalize(data, Schemas.BRAND_GROUP)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateBrandGroup(
  brandGroup: BrandGroupModel,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      brand_group: brandGroup,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/brand_groups/${brandGroup.id}.json`, options, config)
      .then(({ data }: { data: BrandGroupModel }) => {
        const normalizedJson = normalize(data, Schemas.BRAND_GROUP)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: BrandGroupModuleState = initialState,
  action: Action = { type: '' },
) {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors,
      }
    default:
      return state
  }
}
